import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import ReactPaginate from 'react-paginate';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { axios } from '../libs';
import { useTranslation } from '../i18n/I18nContext';
import logger from '../utils/logger';
import { ConversationDataTable } from '../components/Conversation/ConversationDataTable';
import { VariableBadgeFilter } from '../components/Conversation/VariableBadgeFilter';
import { DeleteByPeriodModal } from '../components/Conversation/DeleteByPeriodModal';
import { GenerateLeadsModal } from '../components/Conversation/GenerateLeadsModal';
import {
  applyColumnOrder,
  applyHasDataFilter,
  applyPeriodFilter,
  applySearch,
  buildBadgeVariableNames,
  buildDetailGridTemplate,
  buildOrderedColumns,
  buildPeriodOptions,
  cellHasGraphContent,
  compareConversationRows,
  dataFieldToVariable,
  dedupeRowsByEmail,
  filterActiveDateColumn,
  formatCellValue,
  formatRowDataSummary,
  getActiveDateField,
  getBadgeLabel,
  getConversationAvailableDates,
  getNextGraphColumnVariable,
  isDateColumn,
  isGraphDropoffRow,
  isGraphErrorColumn,
  PINNED_BADGE_VARIABLES,
  sanitizeHiddenColumns,
  sanitizePeriod,
} from '../components/Conversation/conversationFilters';
import {
  loadActiveViewId,
  loadColumnOrder,
  loadFilteredMode,
  loadHasDataVars,
  loadHiddenColumns,
  loadLastSyncAt,
  loadPeriod,
  loadShowActions,
  loadShowActivity,
  loadShowCollectable,
  loadShowGraph,
  loadGraphHighlightVar,
  saveActiveViewId,
  saveColumnOrder,
  saveFilteredMode,
  saveHasDataVars,
  saveHiddenColumns,
  saveLastSyncAt,
  savePeriod,
  saveShowActions,
  saveShowActivity,
  saveShowCollectable,
  saveShowGraph,
  saveGraphHighlightVar,
} from '../components/Conversation/conversationStorage';

const normalizeViewFilterState = (fs) => ({
  hiddenColumns: fs?.hiddenColumns || [],
  hasDataVars: fs?.hasDataVars || [],
  columnOrder: fs?.columnOrder || [],
  showActions: !!fs?.showActions,
  showCollectable: fs?.showCollectable !== false,
  showActivity: !!fs?.showActivity,
  showGraph: !!fs?.showGraph,
  graphHighlightVar: fs?.graphHighlightVar || '',
});

const showProgressNoty = (text) => {
  const noty = new window.Noty({
    text,
    theme: 'bootstrap-v4',
    type: 'info',
    layout: 'bottomLeft',
    timeout: false,
  });
  noty.show();
  return noty;
};

const showConfirm = ({ title, text, icon, onYes }) => {
  let n;
  n = window.nConfirm({
    title,
    text,
    icon,
    onNo: () => {
      n?.close();
    },
    onYes: async () => {
      try {
        await onYes();
      } finally {
        n?.close();
      }
    },
  });
};

const SyncExternalLink = ({ spreadsheetUrl, sheetGid }) => (
  <div
    style={{
      position: 'absolute',
      right: 0,
      top: 0,
      width: '30%',
      height: '100%',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '8px',
      zIndex: 1,
    }}
    onClick={(e) => {
      e.stopPropagation();
      if (spreadsheetUrl) {
        const url = sheetGid != null
          ? `${spreadsheetUrl.split('#')[0]}#gid=${sheetGid}`
          : spreadsheetUrl;
        window.open(url, '_blank');
      }
    }}
    onMouseEnter={(e) => {
      const icon = e.currentTarget.querySelector('i');
      if (icon) icon.style.opacity = '1';
    }}
    onMouseLeave={(e) => {
      const icon = e.currentTarget.querySelector('i');
      if (icon) icon.style.opacity = '0';
    }}
  >
    <i
      className="fa-solid fa-external-link"
      style={{
        fontSize: '12px',
        opacity: 0,
        color: '#032097',
        transition: 'opacity 0.2s',
      }}
    />
  </div>
);

const RowDetailPanel = ({ row, columns, chatbotId, hasActions }) => {
  const detailColumns = columns.filter(
    (col) => !isDateColumn(col.dataField) && col.dataField !== '__actions__',
  );
  const gridTemplateColumns = buildDetailGridTemplate(detailColumns.length, hasActions);

  return (
    <div
      className="conversation-row-detail"
      style={{ gridTemplateColumns }}
    >
      <div className="conversation-row-detail-spacer" aria-hidden="true" />
      {detailColumns.map((col) => (
        <div key={col.dataField} className="conversation-row-detail-cell">
          {formatCellValue(row, col, chatbotId) || ''}
        </div>
      ))}
      {hasActions && <div className="conversation-row-detail-spacer" aria-hidden="true" />}
    </div>
  );
};

export const Conversation = () => {
  const { chatbotId } = useParams();
  const { t, locale } = useTranslation();

  const [rawData, setRawData] = useState([]);
  const [variableNames, setVariableNames] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [chatbotName, setChatbotName] = useState('');
  const [loading, setLoading] = useState(true);

  const [hiddenColumns, setHiddenColumns] = useState(() => loadHiddenColumns(chatbotId));
  const [hasDataVars, setHasDataVars] = useState(() => loadHasDataVars(chatbotId));
  const [period, setPeriod] = useState(() => loadPeriod(chatbotId));
  const [filteredMode, setFilteredMode] = useState(() => loadFilteredMode(chatbotId));
  const [columnOrder, setColumnOrder] = useState(() => loadColumnOrder(chatbotId));
  const [showActionsColumn, setShowActionsColumn] = useState(() => loadShowActions(chatbotId));
  const [showCollectable, setShowCollectable] = useState(() => loadShowCollectable(chatbotId));
  const [showActivity, setShowActivity] = useState(() => loadShowActivity(chatbotId));
  const [showGraph, setShowGraph] = useState(() => loadShowGraph(chatbotId));
  const [graphHighlightVar, setGraphHighlightVar] = useState(() => loadGraphHighlightVar(chatbotId));
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(() => loadActiveViewId(chatbotId));
  const [saveViewModalVisible, setSaveViewModalVisible] = useState(false);
  const [generateLeadsModalVisible, setGenerateLeadsModalVisible] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [savingView, setSavingView] = useState(false);
  const initialViewAppliedRef = useRef(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sizePerPage, setSizePerPage] = useState(100);
  const [sortField, setSortField] = useState(() => getActiveDateField(loadShowActivity(chatbotId)));
  const [sortOrder, setSortOrder] = useState('desc');

  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState(() => loadLastSyncAt(chatbotId));
  const [spreadsheetUrl, setSpreadsheetUrl] = useState(null);
  const [sheetGid, setSheetGid] = useState(null);
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameOldName, setRenameOldName] = useState('');
  const [renameNewName, setRenameNewName] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [deleteByPeriodVisible, setDeleteByPeriodVisible] = useState(false);
  const [periodDeleteStart, setPeriodDeleteStart] = useState('');
  const [periodDeleteEnd, setPeriodDeleteEnd] = useState('');
  const [periodDeleteDateError, setPeriodDeleteDateError] = useState({});
  const [deletingByPeriod, setDeletingByPeriod] = useState(false);

  const conversationAvailableDates = useMemo(
    () => getConversationAvailableDates(rawData),
    [rawData],
  );

  const periodOptions = useMemo(() => buildPeriodOptions(locale), [locale]);

  const formatDateTime = (date) => (date ? moment(date).format('D.M.YYYY HH:mm') : '');
  const formatDate = (dateStr) => (dateStr ? moment(dateStr).format('D.M.YYYY') : '');

  const handlePeriodStartChange = useCallback((value) => {
    if (!value) {
      setPeriodDeleteStart('');
      setPeriodDeleteDateError((prev) => ({ ...prev, start: undefined }));
      return;
    }
    if (!conversationAvailableDates.dateSet.has(value)) {
      setPeriodDeleteStart('');
      setPeriodDeleteDateError((prev) => ({
        ...prev,
        start: t('conversation.invalidPeriodDate'),
      }));
      return;
    }
    setPeriodDeleteStart(value);
    setPeriodDeleteDateError((prev) => ({ ...prev, start: undefined }));
    setPeriodDeleteEnd((prev) => (prev && prev < value ? '' : prev));
  }, [conversationAvailableDates.dateSet, t]);

  const handlePeriodEndChange = useCallback((value) => {
    if (!value) {
      setPeriodDeleteEnd('');
      setPeriodDeleteDateError((prev) => ({ ...prev, end: undefined }));
      return;
    }
    if (!conversationAvailableDates.dateSet.has(value)) {
      setPeriodDeleteEnd('');
      setPeriodDeleteDateError((prev) => ({
        ...prev,
        end: t('conversation.invalidPeriodDate'),
      }));
      return;
    }
    setPeriodDeleteEnd(value);
    setPeriodDeleteDateError((prev) => ({ ...prev, end: undefined }));
  }, [conversationAvailableDates.dateSet, t]);

  const buildColumns = useCallback((columns, occurance) => {
    const filtered = columns.filter((a) => !a.includes('fileName-'));
    const columnData = filtered.map((i) => {
      if (i === 'booking') {
        return {
          dataField: 'data.booking',
          text: i,
          formatter: (_a, row) =>
            `EventId: ${row.data?.booking?.event_id || ''} | ProjectId: ${row.data?.booking?.project_id || ''}`,
        };
      }
      if (i === 'chatProgressId') {
        return {
          dataField: 'chatProgressId',
          text: i,
          formatter: (value) =>
            value ? `https://app.hugo.legal/hugoapp?bot=${chatbotId}&pid=${value}` : '',
        };
      }
      return {
        dataField: `data.${i}`,
        text: occurance ? `${i}` : i,
        formatter: (value) => (value ? value : ''),
      };
    });

    columnData.unshift({
      dataField: 'createdAt',
      text: t('conversation.dateTime'),
      classes: 'cell-date',
      formatter: (value) => (value ? moment(value).format('D.M.YYYY HH:mm') : ''),
    });
    columnData.splice(1, 0, {
      dataField: 'updatedAt',
      text: t('conversation.updated'),
      classes: 'cell-date',
      formatter: (value) => (value ? moment(value).format('D.M.YYYY HH:mm') : ''),
    });
    columnData.push({ dataField: '_id', text: '_id' });
    return columnData;
  }, [chatbotId, t]);

  const refreshData = useCallback(async () => {
    const { conversations, columns, occurance } = await axios.get(
      `/chat/${chatbotId}/en?project=-messages -userIp`,
    );
    const built = buildColumns(columns, occurance);
    setAllColumns(built);
    setVariableNames(buildBadgeVariableNames(columns));
    setRawData(conversations);
  }, [chatbotId, buildColumns]);

  useEffect(() => {
    const loaded = sanitizeHiddenColumns(loadHiddenColumns(chatbotId));
    setHiddenColumns(loaded);
    saveHiddenColumns(chatbotId, loaded);
    setHasDataVars(loadHasDataVars(chatbotId));
    const loadedPeriod = sanitizePeriod(loadPeriod(chatbotId), periodOptions);
    setPeriod(loadedPeriod);
    savePeriod(chatbotId, loadedPeriod);
    setFilteredMode(loadFilteredMode(chatbotId));
    setColumnOrder(loadColumnOrder(chatbotId));
    setShowActionsColumn(loadShowActions(chatbotId));
    setShowCollectable(loadShowCollectable(chatbotId));
    setShowActivity(loadShowActivity(chatbotId));
    setShowGraph(loadShowGraph(chatbotId));
    setGraphHighlightVar(loadGraphHighlightVar(chatbotId));
    setActiveViewId(loadActiveViewId(chatbotId));
    initialViewAppliedRef.current = false;
    setLastSyncAt(loadLastSyncAt(chatbotId));
    setCurrentPage(1);
  }, [chatbotId, periodOptions]);

  useEffect(() => {
    const fetchViews = async () => {
      try {
        const response = await axios.get(`/chat/${chatbotId}/views`);
        const list = response?.views;
        setSavedViews(Array.isArray(list) ? list : []);
      } catch (error) {
        logger.error(error);
        setSavedViews([]);
      }
    };
    fetchViews();
  }, [chatbotId]);

  const applyFilterState = useCallback((filterState) => {
    const fs = normalizeViewFilterState(filterState);
    const hidden = sanitizeHiddenColumns(fs.hiddenColumns, variableNames);
    setHiddenColumns(hidden);
    saveHiddenColumns(chatbotId, hidden);
    setHasDataVars(fs.hasDataVars);
    saveHasDataVars(chatbotId, fs.hasDataVars);
    setColumnOrder(fs.columnOrder);
    saveColumnOrder(chatbotId, fs.columnOrder);
    setShowActionsColumn(fs.showActions);
    saveShowActions(chatbotId, fs.showActions);
    setShowCollectable(fs.showCollectable);
    saveShowCollectable(chatbotId, fs.showCollectable);
    setShowActivity(fs.showActivity);
    saveShowActivity(chatbotId, fs.showActivity);
    setSortField((sf) => {
      if (sf === 'createdAt' || sf === 'updatedAt') {
        return getActiveDateField(fs.showActivity);
      }
      return sf;
    });
    setSortOrder('desc');
    setShowGraph(fs.showGraph);
    saveShowGraph(chatbotId, fs.showGraph);
    setGraphHighlightVar(fs.graphHighlightVar);
    saveGraphHighlightVar(chatbotId, fs.graphHighlightVar);
    setCurrentPage(1);
  }, [chatbotId, variableNames]);

  useEffect(() => {
    if (initialViewAppliedRef.current || !savedViews.length) return;
    const storedId = loadActiveViewId(chatbotId);
    if (!storedId) return;
    const view = savedViews.find((v) => String(v._id) === String(storedId));
    if (!view?.filterState) return;
    initialViewAppliedRef.current = true;
    applyFilterState(view.filterState);
    setActiveViewId(storedId);
  }, [chatbotId, savedViews, applyFilterState]);

  useEffect(() => {
    if (!variableNames.length) return;
    setHiddenColumns((prev) => {
      const next = sanitizeHiddenColumns(prev, variableNames);
      if (next.length !== prev.length || next.some((v, i) => v !== prev[i])) {
        saveHiddenColumns(chatbotId, next);
        return next;
      }
      return prev;
    });
  }, [chatbotId, variableNames]);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        await refreshData();
        try {
          const { chatbot } = await axios.get(`/chatbot/${chatbotId}`);
          setChatbotName(chatbot?.name || '');
        } catch {
          setChatbotName('');
        }
      } catch (error) {
        logger.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [chatbotId, refreshData]);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get('/crm/settings');
        const data = response.data || response;
        if (data.spreadsheetUrl) {
          setSpreadsheetUrl(data.spreadsheetUrl);
        } else if (data.spreadsheetId) {
          setSpreadsheetUrl(`https://docs.google.com/spreadsheets/d/${data.spreadsheetId}/edit`);
        }
      } catch (error) {
        logger.error('Error loading CRM settings:', error);
      }
    };
    loadSettings();
  }, []);

  const orderedVariableNames = useMemo(
    () => applyColumnOrder(variableNames, columnOrder),
    [variableNames, columnOrder],
  );

  const visibleColumns = useMemo(() => {
    const baseCols = filteredMode
      ? buildOrderedColumns(allColumns, variableNames, columnOrder, hiddenColumns)
      : allColumns;
    return filterActiveDateColumn(baseCols, showActivity);
  }, [filteredMode, allColumns, variableNames, columnOrder, hiddenColumns, showActivity]);

  const filteredData = useMemo(() => {
    let rows = applyPeriodFilter(rawData, period);
    if (filteredMode) {
      rows = applyHasDataFilter(rows, hasDataVars);
    }
    rows = applySearch(rows, search, visibleColumns, chatbotId);
    return rows;
  }, [rawData, period, filteredMode, hasDataVars, search, visibleColumns, chatbotId]);

  const currentFilterState = useMemo(
    () => normalizeViewFilterState({
      hiddenColumns,
      hasDataVars,
      columnOrder,
      showActions: showActionsColumn,
      showCollectable,
      showActivity,
      showGraph,
      graphHighlightVar,
    }),
    [
      hiddenColumns,
      hasDataVars,
      columnOrder,
      showActionsColumn,
      showCollectable,
      showActivity,
      showGraph,
      graphHighlightVar,
    ],
  );

  const isViewDirty = useMemo(() => {
    if (!activeViewId) return false;
    const view = savedViews.find((v) => String(v._id) === String(activeViewId));
    if (!view?.filterState) return false;
    return JSON.stringify(currentFilterState) !== JSON.stringify(normalizeViewFilterState(view.filterState));
  }, [activeViewId, savedViews, currentFilterState]);

  const sortedData = useMemo(() => {
    const rows = [...filteredData];
    rows.sort((a, b) => compareConversationRows(a, b, sortField, sortOrder, chatbotId));
    return rows;
  }, [filteredData, sortField, sortOrder, chatbotId]);

  const totalRecords = sortedData.length;
  const pageCount = Math.ceil(totalRecords / sizePerPage) || 1;
  const pageData = useMemo(() => {
    const start = (currentPage - 1) * sizePerPage;
    return sortedData.slice(start, start + sizePerPage);
  }, [sortedData, currentPage, sizePerPage]);

  useEffect(() => {
    setExpandedRowId(null);
  }, [
    currentPage,
    filteredMode,
    showGraph,
    graphHighlightVar,
    hasDataVars,
    hiddenColumns,
    columnOrder,
    period,
    search,
  ]);

  const nextHighlightVar = useMemo(
    () => (graphHighlightVar
      ? getNextGraphColumnVariable(visibleColumns, graphHighlightVar)
      : null),
    [visibleColumns, graphHighlightVar],
  );

  const graphDropoffRowsForDelete = useMemo(() => {
    if (!graphHighlightVar || !nextHighlightVar) return [];
    return sortedData.filter((row) => isGraphDropoffRow(row, graphHighlightVar, nextHighlightVar));
  }, [sortedData, graphHighlightVar, nextHighlightVar]);

  const graphDropoffRows = useMemo(
    () => dedupeRowsByEmail(graphDropoffRowsForDelete),
    [graphDropoffRowsForDelete],
  );

  const canDeleteDropoff = filteredMode && showGraph && graphHighlightVar && nextHighlightVar;

  const graphDropoffIds = useMemo(
    () => graphDropoffRows.map((row) => row._id),
    [graphDropoffRows],
  );

  const handleTableChange = (type, { sortField: field, sortOrder: order }) => {
    if (type === 'sort' && field) {
      setSortField(field);
      setSortOrder(order);
      setCurrentPage(1);
    }
  };

  const handleToggleVisibility = (variable) => {
    if (PINNED_BADGE_VARIABLES.includes(variable)) return;
    setHiddenColumns((prev) => {
      const next = prev.includes(variable)
        ? prev.filter((v) => v !== variable)
        : [...prev, variable];
      saveHiddenColumns(chatbotId, next);
      return next;
    });
  };

  const handleToggleHasData = (variable) => {
    setHasDataVars((prev) => {
      const next = prev.includes(variable)
        ? prev.filter((v) => v !== variable)
        : [...prev, variable];
      saveHasDataVars(chatbotId, next);
      return next;
    });
    setCurrentPage(1);
  };

  const handlePeriodChange = (value) => {
    const next = sanitizePeriod(value, periodOptions);
    setPeriod(next);
    savePeriod(chatbotId, next);
    setCurrentPage(1);
  };

  const handleToggleFilteredMode = () => {
    setFilteredMode((prev) => {
      const next = !prev;
      saveFilteredMode(chatbotId, next);
      return next;
    });
    setCurrentPage(1);
  };

  const handleReorderColumns = (oldIndex, newIndex) => {
    const ordered = applyColumnOrder(variableNames, columnOrder);
    const next = [...ordered];
    const [removed] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, removed);
    setColumnOrder(next);
    saveColumnOrder(chatbotId, next);
  };

  const handleToggleActionsColumn = () => {
    setShowActionsColumn((prev) => {
      const next = !prev;
      saveShowActions(chatbotId, next);
      return next;
    });
  };

  const handleToggleShowCollectable = () => {
    setShowCollectable((prev) => {
      const next = !prev;
      saveShowCollectable(chatbotId, next);
      return next;
    });
  };

  const handleToggleActivity = () => {
    setShowActivity((prev) => {
      const next = !prev;
      saveShowActivity(chatbotId, next);
      const activeDateField = getActiveDateField(next);
      setSortField(activeDateField);
      setSortOrder('desc');
      setCurrentPage(1);
      return next;
    });
  };

  const handleToggleGraph = () => {
    setShowGraph((prev) => {
      const next = !prev;
      saveShowGraph(chatbotId, next);
      if (!next) {
        setGraphHighlightVar('');
        saveGraphHighlightVar(chatbotId, '');
      }
      return next;
    });
  };

  const handleGraphHeaderClick = useCallback((variable) => {
    setGraphHighlightVar((prev) => {
      const next = prev === variable ? '' : variable;
      saveGraphHighlightVar(chatbotId, next);
      return next;
    });
  }, [chatbotId]);

  const handleLoadView = (viewId) => {
    setActiveViewId(viewId || '');
    saveActiveViewId(chatbotId, viewId || '');
    if (!viewId) {
      const loaded = sanitizeHiddenColumns(loadHiddenColumns(chatbotId));
      setHiddenColumns(loaded);
      saveHiddenColumns(chatbotId, loaded);
      setHasDataVars(loadHasDataVars(chatbotId));
      setColumnOrder(loadColumnOrder(chatbotId));
      setShowActionsColumn(loadShowActions(chatbotId));
      setShowCollectable(loadShowCollectable(chatbotId));
      setShowActivity(loadShowActivity(chatbotId));
      setShowGraph(loadShowGraph(chatbotId));
      setGraphHighlightVar(loadGraphHighlightVar(chatbotId));
      setCurrentPage(1);
      return;
    }
    const view = savedViews.find((v) => String(v._id) === String(viewId));
    if (view?.filterState) {
      applyFilterState(view.filterState);
    }
  };

  const handleSaveView = async () => {
    if (!activeViewId) return;
    try {
      setSavingView(true);
      const response = await axios.put(`/chat/${chatbotId}/views/${activeViewId}`, {
        filterState: currentFilterState,
      });
      const view = response?.view;
      if (view) {
        setSavedViews((prev) => prev.map((v) => (
          String(v._id) === String(view._id) ? view : v
        )));
      }
      window.nSuccess({ title: t('common.successTitle'), text: t('conversation.viewSaved'), icon: 'fa-check' });
    } catch (error) {
      logger.error(error);
      window.nError({
        text: error?.error || error?.message || t('common.errorTitle'),
      });
    } finally {
      setSavingView(false);
    }
  };

  const handleSaveViewAsSubmit = async (e) => {
    e.preventDefault();
    const name = saveViewName.trim();
    if (!name) {
      window.nError({ text: t('conversation.viewNameRequired') });
      return;
    }
    try {
      setSavingView(true);
      const response = await axios.post(`/chat/${chatbotId}/views`, {
        name,
        filterState: currentFilterState,
      });
      const view = response?.view;
      if (view) {
        setSavedViews((prev) => [...prev, view].sort((a, b) => a.name.localeCompare(b.name)));
        setActiveViewId(String(view._id));
        saveActiveViewId(chatbotId, String(view._id));
      }
      setSaveViewModalVisible(false);
      setSaveViewName('');
      window.nSuccess({ title: t('common.successTitle'), text: t('conversation.viewSaved'), icon: 'fa-check' });
    } catch (error) {
      logger.error(error);
      window.nError({
        text: error?.error || error?.message || t('common.errorTitle'),
      });
    } finally {
      setSavingView(false);
    }
  };

  const handleDeleteView = () => {
    if (!activeViewId) return;
    showConfirm({
      title: t('conversation.deleteTitle'),
      text: t('conversation.viewDelete'),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        await axios.delete(`/chat/${chatbotId}/views/${activeViewId}`);
        setSavedViews((prev) => prev.filter((v) => String(v._id) !== String(activeViewId)));
        handleLoadView('');
        window.nSuccess({ text: t('conversation.viewDeleted') });
      },
    });
  };

  const recordSyncSuccess = useCallback(() => {
    const now = new Date().toISOString();
    saveLastSyncAt(chatbotId, now);
    setLastSyncAt(now);
  }, [chatbotId]);

  const syncToSheets = useCallback(async ({ openTab = true, showToast = true } = {}) => {
    const response = await axios.post(`/chat/${chatbotId}/sync-to-sheets`);
    const responseData = response.data || response;
    const rowsWritten = responseData?.rowsWritten ?? 0;
    const syncedSheetName = responseData?.sheetName || '';
    let url = responseData?.spreadsheetUrl;
    if (!url && responseData?.spreadsheetId) {
      url = `https://docs.google.com/spreadsheets/d/${responseData.spreadsheetId}/edit`;
    }
    if (url) {
      const baseUrl = url.split('#')[0];
      setSpreadsheetUrl(baseUrl);
      if (responseData?.sheetGid != null) {
        setSheetGid(responseData.sheetGid);
        if (openTab) {
          window.open(`${baseUrl}#gid=${responseData.sheetGid}`, '_blank');
        }
      }
    }
    recordSyncSuccess();
    if (showToast) {
      let message;
      if (rowsWritten > 0 && syncedSheetName) {
        message = t('conversation.syncSuccessWithTab', { count: rowsWritten, sheetName: syncedSheetName });
      } else if (responseData?.message) {
        message = responseData.message;
      } else if (syncedSheetName) {
        message = t('conversation.syncAlreadyUpToDate', { sheetName: syncedSheetName });
      } else {
        message = t('conversation.syncSuccess');
      }
      window.nSuccess({ title: t('common.successTitle'), text: message, icon: 'fa-check' });
    }
    return responseData;
  }, [chatbotId, recordSyncSuccess, t]);

  const runBulkDeleteWithSync = useCallback(async ({ onDelete, getSuccessMessage }) => {
    let progressNoty;
    try {
      setSyncing(true);
      progressNoty = showProgressNoty(t('conversation.syncingBeforeDelete'));
      try {
        await syncToSheets({ openTab: false, showToast: false });
      } catch (syncError) {
        logger.error(syncError);
        progressNoty.close();
        const syncErrorMessage = syncError?.response?.data?.error
          || syncError?.response?.data?.message
          || syncError?.message
          || t('conversation.syncError');
        window.nError({
          title: t('common.syncError'),
          text: `${t('conversation.deleteAbortedSyncFailed')} ${syncErrorMessage}`,
          icon: 'fa-exclamation-triangle',
        });
        return;
      }
      progressNoty.setText(t('conversation.deletingData'));
      const result = await onDelete();
      progressNoty.close();
      if (getSuccessMessage) {
        const successMessage = getSuccessMessage(result);
        if (successMessage) {
          window.nSuccess(
            typeof successMessage === 'string'
              ? { text: successMessage }
              : successMessage,
          );
        }
      }
    } catch (error) {
      logger.error(error);
      progressNoty?.close();
      throw error;
    } finally {
      setSyncing(false);
    }
  }, [syncToSheets, t]);

  const handleDeleteRow = useCallback((row) => {
    const summary = formatRowDataSummary(row, visibleColumns, chatbotId);
    const text = `<div style="text-align:left;margin-top:12px;">
      <p>${t('conversation.deleteRowConfirm')}</p>
      <p><strong>${t('conversation.availableData')}</strong></p>
      <div>${summary || '—'}</div>
    </div>`;

    showConfirm({
      title: t('conversation.deleteTitle'),
      text,
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        await axios.delete(`/chat/${chatbotId}/delete/conversation/${row._id}`);
        await refreshData();
        window.nSuccess({ text: t('common.delete') });
      },
    });
  }, [chatbotId, refreshData, t, visibleColumns]);

  const handleDeleteByPeriodSubmit = () => {
    if (!periodDeleteStart || !periodDeleteEnd || periodDeleteEnd < periodDeleteStart) return;

    const startAt = moment(periodDeleteStart).startOf('day').toISOString();
    const endAt = moment(periodDeleteEnd).endOf('day').toISOString();
    const startLabel = formatDate(periodDeleteStart);
    const endLabel = formatDate(periodDeleteEnd);
    const confirmText = t('conversation.deleteByPeriodConfirm')
      .replace('{{start}}', startLabel)
      .replace('{{end}}', endLabel);

    setDeleteByPeriodVisible(false);

    showConfirm({
      title: t('conversation.deleteTitle'),
      text: `<div style="text-align:left;margin-top:12px;">${confirmText}</div>`,
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          setDeletingByPeriod(true);
          await runBulkDeleteWithSync({
            onDelete: async () => {
              const { deletedCount } = await axios.delete(
                `/chat/${chatbotId}/delete/conversation/by-period`,
                { startAt, endAt },
              );
              await refreshData();
              setPeriodDeleteStart('');
              setPeriodDeleteEnd('');
              setPeriodDeleteDateError({});
              return deletedCount;
            },
            getSuccessMessage: (deletedCount) => ({
              text: t('conversation.deleteByPeriodSuccess', { count: deletedCount || 0 }),
            }),
          });
        } catch (error) {
          window.nError({ text: t('conversation.deleteByPeriodError') });
        } finally {
          setDeletingByPeriod(false);
        }
      },
    });
  };

  const effectiveShowActions = filteredMode && (showGraph || showActionsColumn);
  const effectiveShowGraph = filteredMode && showGraph;
  const effectiveShowCollectable = filteredMode && showCollectable;

  const dateColumnStyle = { width: '150px', minWidth: '150px', maxWidth: '150px' };

  const tableColumns = useMemo(() => {
    const cols = visibleColumns.map((col) => {
      if (effectiveShowGraph && isDateColumn(col.dataField)) {
        return {
          ...col,
          style: { ...col.style, ...dateColumnStyle },
          headerStyle: { ...col.headerStyle, ...dateColumnStyle },
        };
      }
      if (!effectiveShowGraph || isDateColumn(col.dataField)) return col;
      const variable = dataFieldToVariable(col.dataField);
      const isHeaderActive = graphHighlightVar === variable;
      const useErrorIcon = isGraphErrorColumn(variable);
      return {
        ...col,
        sort: false,
        classes: `${col.classes || ''} cell-graph`.trim(),
        headerClasses: `${col.headerClasses || ''} cell-graph${isHeaderActive ? ' cell-graph-header-active' : ''}`.trim(),
        headerFormatter: () => (
          <button
            type="button"
            className={`cell-graph-header${isHeaderActive ? ' is-active' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              handleGraphHeaderClick(variable);
            }}
            title={t('conversation.graphDisplay.highlightColumn')}
          >
            {col.text}
          </button>
        ),
        formatter: (_cell, row) => {
          if (!cellHasGraphContent(row, col, chatbotId)) return null;
          const text = formatCellValue(row, col, chatbotId);
          const rowHighlighted = graphHighlightVar
            && isGraphDropoffRow(row, graphHighlightVar, nextHighlightVar);

          if (useErrorIcon) {
            return (
              <div
                className={`cell-graph-inner cell-graph-error-wrap${rowHighlighted ? ' is-highlighted' : ''}`}
                title={text}
              >
                <i className="fa-solid fa-triangle-exclamation cell-graph-error" />
              </div>
            );
          }

          return (
            <div className="cell-graph-inner" title={text}>
              <div className={`cell-graph-bar${rowHighlighted ? ' is-highlighted' : ''}`} />
            </div>
          );
        },
      };
    });

    if (!effectiveShowActions) return cols;

    const actionsColumn = {
      dataField: '__actions__',
      text: '',
      sort: false,
      classes: 'cell-actions',
      headerClasses: 'cell-actions',
      style: { width: '100px', minWidth: '100px', maxWidth: '100px' },
      headerStyle: { width: '100px', minWidth: '100px', maxWidth: '100px' },
      formatter: (_cell, row) => (
        <div className="actions">
          <button
            type="button"
            className="button button-small"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteRow(row);
            }}
          >
            {t('common.delete')}
          </button>
        </div>
      ),
    };

    return effectiveShowGraph ? [...cols, actionsColumn] : [actionsColumn, ...cols];
  }, [
    visibleColumns,
    effectiveShowGraph,
    effectiveShowActions,
    graphHighlightVar,
    nextHighlightVar,
    chatbotId,
    handleDeleteRow,
    handleGraphHeaderClick,
    t,
  ]);

  const handleRowClick = useCallback((e, row) => {
    if (e.target.closest('button, a, .cell-actions')) return;
    setExpandedRowId((prev) => (prev === row._id ? null : row._id));
  }, []);

  const tableExpandRow = useMemo(() => {
    if (!filteredMode) return null;
    return {
      showExpandColumn: false,
      expanded: expandedRowId ? [expandedRowId] : [],
      parentClassName: 'table-row-expanded',
      renderer: (row) => (
        <RowDetailPanel
          row={row}
          columns={visibleColumns}
          chatbotId={chatbotId}
          hasActions={effectiveShowActions}
        />
      ),
    };
  }, [filteredMode, expandedRowId, visibleColumns, chatbotId, effectiveShowActions]);

  const tableRowEvents = useMemo(() => {
    if (!filteredMode) return null;
    return { onClick: handleRowClick };
  }, [filteredMode, handleRowClick]);

  const getExportRows = () =>
    sortedData.map((row) => {
      const flat = {};
      visibleColumns.forEach((col) => {
        flat[col.text] = formatCellValue(row, col, chatbotId);
      });
      return flat;
    });

  const handleExportCsv = () => {
    const rows = getExportRows();
    if (!rows.length) {
      window.nError({ text: t('conversation.noDataToExport') });
      return;
    }
    const headers = visibleColumns.map((c) => c.text);
    const escape = (val) => {
      const str = val == null ? '' : String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const lines = [
      headers.map(escape).join(','),
      ...rows.map((row) => headers.map((h) => escape(row[h])).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `conversation-${chatbotId}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExportExcel = () => {
    try {
      const rows = getExportRows();
      if (!rows.length) {
        window.nError({ text: t('conversation.noDataToExport') });
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
      XLSX.writeFile(workbook, `conversation-${chatbotId}.xlsx`);
    } catch (error) {
      logger.error('Error exporting to Excel:', error);
    }
  };

  const handleSyncToSheets = async () => {
    try {
      setSyncing(true);
      await syncToSheets({ openTab: true, showToast: true });
    } catch (error) {
      logger.error('Error syncing to sheets:', error);
      const errorMessage = error?.response?.data?.message || error?.message || t('conversation.syncError');
      window.nError({ title: t('common.syncError'), text: errorMessage, icon: 'fa-exclamation-triangle' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteDataSheet = () => {
    setShowActionMenu(false);
    showConfirm({
      title: t('conversation.deleteTitle'),
      text: t('conversation.deleteConfirm'),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          await runBulkDeleteWithSync({
            onDelete: async () => {
              const { ok } = await axios.delete(`/chat/${chatbotId}/delete/conversation`);
              if (ok) {
                setRawData([]);
                setVariableNames([]);
                setAllColumns([]);
              }
              await refreshData();
            },
            getSuccessMessage: () => ({ text: t('common.delete') }),
          });
        } catch (error) {
          logger.error(error);
          window.nError({ text: error?.message || t('conversation.syncError') });
        }
      },
    });
  };

  const handleDeleteFiltered = () => {
    setShowActionMenu(false);
    const count = sortedData.length;
    if (count === 0) {
      window.nError({ text: t('conversation.deleteFilteredEmpty') });
      return;
    }
    showConfirm({
      title: t('conversation.deleteTitle'),
      text: t('conversation.deleteFilteredConfirm', { count }),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          const conversationIds = sortedData.map((row) => row._id);
          await runBulkDeleteWithSync({
            onDelete: async () => {
              const { deletedCount } = await axios.delete(
                `/chat/${chatbotId}/delete/conversation/filtered`,
                { conversationIds },
              );
              await refreshData();
              setCurrentPage(1);
              return deletedCount;
            },
            getSuccessMessage: (deletedCount) => ({
              text: t('conversation.deleteFilteredSuccess', { count: deletedCount || 0 }),
            }),
          });
        } catch (error) {
          window.nError({ text: t('conversation.deleteFilteredError') });
        }
      },
    });
  };

  const handleDeleteDropoff = () => {
    setShowActionMenu(false);
    const count = graphDropoffRowsForDelete.length;
    if (count === 0) {
      window.nError({ text: t('conversation.deleteFilteredEmpty') });
      return;
    }
    const column = getBadgeLabel(graphHighlightVar, t);
    const nextColumn = getBadgeLabel(nextHighlightVar, t);
    showConfirm({
      title: t('conversation.deleteTitle'),
      text: t('conversation.deleteDropoffConfirm', { count, column, nextColumn }),
      icon: 'fa-exclamation-circle',
      onYes: async () => {
        try {
          const conversationIds = graphDropoffRowsForDelete.map((row) => row._id);
          await runBulkDeleteWithSync({
            onDelete: async () => {
              const { deletedCount } = await axios.delete(
                `/chat/${chatbotId}/delete/conversation/filtered`,
                { conversationIds },
              );
              await refreshData();
              setCurrentPage(1);
              return deletedCount;
            },
            getSuccessMessage: (deletedCount) => ({
              text: t('conversation.deleteDropoffSuccess', { count: deletedCount || 0 }),
            }),
          });
        } catch (error) {
          window.nError({ text: t('conversation.deleteFilteredError') });
        }
      },
    });
  };

  const handleRenameSubmit = async (e) => {
    e.preventDefault();
    if (!renameOldName.trim() || !renameNewName.trim()) return;
    try {
      setRenaming(true);
      await axios.put(`/chat/${chatbotId}/renamevariable`, {
        oldName: renameOldName.trim(),
        newName: renameNewName.trim(),
      });
      await refreshData();
      window.nSuccess({ text: t('conversation.renameSuccess') });
      setRenameModalVisible(false);
      setRenameOldName('');
      setRenameNewName('');
    } catch (error) {
      logger.error(error);
      window.nError({ text: t('conversation.renameError') });
    } finally {
      setRenaming(false);
    }
  };

  const noDataMessage = rawData.length
    ? t('conversation.noMatchingData')
    : t('conversation.noData');

  return (
      <div className={`row table_v2 conversation-datasheet${filteredMode ? ' filtered-mode' : ''}${effectiveShowGraph ? ' graph-mode' : ''}`}>
        <div className="col p-0">
          <div className="x_panel chatbot-builder">
            <div className="header-menu d-flex justify-content-between align-items-center">
              <div className="header-title d-flex align-items-center">
                <h1>{chatbotName || t('conversation.title')}</h1>
              </div>
              <div className="header-actions d-flex align-items-center">
                <div className="filter filter-toggle d-flex align-items-center conversation-header-filtered">
                  <span>{t('conversation.filtered')}</span>
                  <div className="btn-switch" onClick={handleToggleFilteredMode}>
                    <div className={`switch ${filteredMode ? 'on' : 'off'}`}>
                      <span />
                    </div>
                  </div>
                </div>
                <button type="button" className="button button-small" onClick={handleExportCsv}>
                  {t('conversation.exportCsv')}
                </button>
                <button type="button" className="button button-small" onClick={handleExportExcel} style={{ marginLeft: '8px' }}>
                  {t('conversation.exportExcel')}
                </button>
                <div className="sync-to-sheets-wrap" style={{ position: 'relative', marginLeft: '10px', marginRight: '10px' }}>
                  <button
                    type="button"
                    className="button button-small"
                    onClick={handleSyncToSheets}
                    disabled={syncing}
                    style={{ position: 'relative', paddingRight: '35px' }}
                  >
                    {syncing ? t('common.syncing') : t('conversation.syncToSheets')}
                  </button>
                  <SyncExternalLink spreadsheetUrl={spreadsheetUrl} sheetGid={sheetGid} />
                </div>
                <div className="action action-search">
                  <input
                    type="text"
                    placeholder={t('common.enterKeyword')}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  <i className="fa-solid fa-magnifying-glass" />
                </div>
                <div className="action action-more">
                  <button
                    type="button"
                    className={`button toggle${showActionMenu ? ' active' : ''}`}
                    onClick={() => setShowActionMenu((open) => !open)}
                  >
                    ...
                  </button>
                  {showActionMenu && (
                    <div className="dropdown-more">
                      <button
                        type="button"
                        className="item"
                        onClick={() => {
                          setRenameModalVisible(true);
                          setShowActionMenu(false);
                        }}
                      >
                        {t('conversation.renameVariable')}
                      </button>
                      <button type="button" className="item" onClick={handleDeleteDataSheet}>
                        {t('conversation.deleteDatasheet')}
                      </button>
                      <button
                        type="button"
                        className="item"
                        onClick={() => {
                          setDeleteByPeriodVisible(true);
                          setShowActionMenu(false);
                        }}
                      >
                        {t('conversation.deleteByPeriod')}
                      </button>
                      <button type="button" className="item" onClick={handleDeleteFiltered}>
                        {t('conversation.deleteFiltered')}
                      </button>
                      {canDeleteDropoff && (
                        <button type="button" className="item" onClick={handleDeleteDropoff}>
                          {t('conversation.deleteDropoff')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="x_content">
              <div className="page-container d-flex flex-column">
                {loading ? (
                  <div>{t('common.loading')}</div>
                ) : (
                  <>
                    <div className="page-header d-flex flex-column">
                      <div className="conversation-filter-row d-flex justify-content-between align-items-center flex-wrap">
                        <div className="filter filter-pagesize d-flex align-items-center flex-wrap">
                          <div className="filter filter-view d-flex align-items-center flex-wrap">
                            <select
                              value={activeViewId || ''}
                              onChange={(e) => handleLoadView(e.target.value)}
                            >
                              <option value="">{t('conversation.viewDefault')}</option>
                              {savedViews.map((v) => (
                                <option key={v._id} value={v._id}>
                                  {v.name}
                                  {String(activeViewId) === String(v._id) && isViewDirty ? ' *' : ''}
                                </option>
                              ))}
                            </select>
                            <div className="action action-more action-view-more">
                              <button
                                type="button"
                                className={`button toggle${showViewMenu ? ' active' : ''}`}
                                onClick={() => setShowViewMenu((open) => !open)}
                              >
                                ...
                              </button>
                              {showViewMenu && (
                                <div className="dropdown-more">
                                  {activeViewId && (
                                    <button
                                      type="button"
                                      className="item"
                                      disabled={savingView}
                                      onClick={() => {
                                        setShowViewMenu(false);
                                        handleSaveView();
                                      }}
                                    >
                                      {savingView ? t('common.saving') : t('conversation.viewSave')}
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    className="item"
                                    onClick={() => {
                                      setShowViewMenu(false);
                                      setSaveViewName('');
                                      setSaveViewModalVisible(true);
                                    }}
                                  >
                                    {t('conversation.viewSaveAs')}
                                  </button>
                                  {activeViewId && (
                                    <button
                                      type="button"
                                      className="item"
                                      onClick={() => {
                                        setShowViewMenu(false);
                                        handleDeleteView();
                                      }}
                                    >
                                      {t('conversation.viewDelete')}
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <select value={period} onChange={(e) => handlePeriodChange(e.target.value)}>
                            {periodOptions.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.labelKey ? t(opt.labelKey) : opt.label}
                              </option>
                            ))}
                          </select>
                          {filteredMode && (
                            <>
                              <div
                                className="filter filter-toggle d-flex align-items-center"
                                title={showGraph ? t('conversation.actionsDisabledInGraph') : undefined}
                              >
                                <span>{t('conversation.actions')}</span>
                                <div
                                  className={`btn-switch${showGraph ? ' disabled' : ''}`}
                                  onClick={showGraph ? undefined : handleToggleActionsColumn}
                                >
                                  <div className={`switch ${showActionsColumn ? 'on' : 'off'}${showGraph ? ' disabled' : ''}`}>
                                    <span />
                                  </div>
                                </div>
                              </div>
                              <div className="filter filter-toggle d-flex align-items-center">
                                <span>{t('conversation.collectable')}</span>
                                <div className="btn-switch" onClick={handleToggleShowCollectable}>
                                  <div className={`switch ${showCollectable ? 'on' : 'off'}`}>
                                    <span />
                                  </div>
                                </div>
                              </div>
                              <div className="filter filter-toggle d-flex align-items-center">
                                <span>{t('conversation.graph')}</span>
                                <div className="btn-switch" onClick={handleToggleGraph}>
                                  <div className={`switch ${showGraph ? 'on' : 'off'}`}>
                                    <span />
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          <div className="filter filter-toggle d-flex align-items-center">
                            <span>{t('conversation.activity')}</span>
                            <div className="btn-switch" onClick={handleToggleActivity}>
                              <div className={`switch ${showActivity ? 'on' : 'off'}`}>
                                <span />
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="conversation-content-meta">
                          <div className="conversation-content-meta-row">
                            {lastSyncAt
                              ? t('conversation.lastSynced', {
                                date: moment(lastSyncAt).format('D.M.YYYY HH:mm'),
                              })
                              : t('conversation.neverSynced')}
                          </div>
                          <div className="conversation-content-meta-row">
                            {conversationAvailableDates.minDate
                              ? t('conversation.totalSinceFirst', {
                                count: rawData.length,
                                date: formatDate(conversationAvailableDates.minDate),
                              })
                              : t('conversation.totalConversations', { count: rawData.length })}
                          </div>
                          <div className="conversation-content-meta-row">
                            {t('conversation.filteredCount', {
                              count: filteredData.length,
                              total: rawData.length,
                            })}
                          </div>
                        </div>
                      </div>
                      {effectiveShowCollectable && (
                        <VariableBadgeFilter
                          variables={orderedVariableNames}
                          hiddenColumns={hiddenColumns}
                          hasDataVars={hasDataVars}
                          onToggleVisibility={handleToggleVisibility}
                          onToggleHasData={handleToggleHasData}
                          onReorder={handleReorderColumns}
                          sortable
                          t={t}
                        />
                      )}
                    </div>
                    <div className="page-content nopaginate">
                      {effectiveShowGraph && graphHighlightVar && graphDropoffRowsForDelete.length > 0 && (
                        <div className="conversation-generate-leads-bar">
                          <span>
                            {t('conversation.generateLeadsCount', { count: graphDropoffRowsForDelete.length })}
                          </span>
                          <button
                            type="button"
                            className="button button-blue"
                            onClick={() => setGenerateLeadsModalVisible(true)}
                          >
                            {t('conversation.generateLeads')}
                          </button>
                          {nextHighlightVar && (
                            <button
                              type="button"
                              className="button button-small"
                              onClick={handleDeleteDropoff}
                            >
                              {t('conversation.deleteDropoff')}
                            </button>
                          )}
                        </div>
                      )}
                      {visibleColumns.length === 0 ? (
                        <div className="conversation-no-columns">
                          <p>{t('conversation.noColumnsVisible')}</p>
                        </div>
                      ) : (
                        <ConversationDataTable
                          tableKey={`${effectiveShowGraph}-${graphHighlightVar}`}
                          data={pageData}
                          columns={tableColumns}
                          onTableChange={handleTableChange}
                          sort={{ dataField: sortField, order: sortOrder }}
                          noDataIndication={noDataMessage}
                          expandRow={tableExpandRow}
                          rowEvents={tableRowEvents}
                        />
                      )}
                    </div>
                    <div className="page-footer d-flex justify-content-between align-items-center">
                      <div className="filter filter-pagination d-flex align-items-center">
                        {pageCount > 1 && (
                          <ReactPaginate
                            containerClassName="pagination"
                            activeClassName="active"
                            previousLinkClassName="fa-regular fa-chevron-left"
                            nextLinkClassName="fa-regular fa-chevron-right"
                            previousClassName={currentPage === 1 ? 'd-none' : 'previous'}
                            nextClassName={currentPage === pageCount ? 'd-none' : 'next'}
                            previousLabel={t('common.previous')}
                            nextLabel={t('common.next')}
                            breakLabel={t('common.ellipsis')}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            pageCount={pageCount}
                            onPageChange={(e) => setCurrentPage(e.selected + 1)}
                            forcePage={currentPage - 1}
                          />
                        )}
                      </div>
                      <div className="filter filter-pagesize d-flex justify-content-end align-items-center">
                        <select
                          value={sizePerPage}
                          onChange={(e) => {
                            setSizePerPage(+e.target.value);
                            setCurrentPage(1);
                          }}
                        >
                          {[100, 200, 300, 500].map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

      <DeleteByPeriodModal
        visible={deleteByPeriodVisible}
        onClose={() => {
          setDeleteByPeriodVisible(false);
          setPeriodDeleteStart('');
          setPeriodDeleteEnd('');
          setPeriodDeleteDateError({});
        }}
        startDate={periodDeleteStart}
        endDate={periodDeleteEnd}
        onStartChange={handlePeriodStartChange}
        onEndChange={handlePeriodEndChange}
        minDate={conversationAvailableDates.minDate}
        maxDate={conversationAvailableDates.maxDate}
        dateError={periodDeleteDateError}
        onSubmit={handleDeleteByPeriodSubmit}
        deleting={deletingByPeriod}
        t={t}
      />

      {saveViewModalVisible && (
        <div className="modal prepayment" onClick={() => setSaveViewModalVisible(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modalclose" onClick={() => setSaveViewModalVisible(false)}>
                &times;
              </span>
              <h2>{t('conversation.viewSaveAs')}</h2>
            </div>
            <div className="modal-form d-flex flex-row flex-wrap">
              <form onSubmit={handleSaveViewAsSubmit} style={{ width: '100%', display: 'contents' }}>
                <div className="form-row col-thin">
                  <label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('conversation.viewName')}</label>
                  <input
                    type="text"
                    value={saveViewName}
                    onChange={(e) => setSaveViewName(e.target.value)}
                    style={{ width: '100%' }}
                    autoFocus
                  />
                </div>
                <div className="form-row col-full row-actions">
                  <button type="button" className="button button-large" onClick={() => setSaveViewModalVisible(false)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="button button-large button-blue"
                    disabled={savingView}
                  >
                    {savingView ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {renameModalVisible && (
        <div className="modal prepayment" onClick={() => setRenameModalVisible(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modalclose" onClick={() => setRenameModalVisible(false)}>
                &times;
              </span>
              <h2>{t('conversation.renameVariable')}</h2>
            </div>
            <div className="modal-form d-flex flex-row flex-wrap">
              <form onSubmit={handleRenameSubmit} style={{ width: '100%', display: 'contents' }}>
                <div className="form-row col-thin">
                  <label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('conversation.renameOldLabel')}</label>
                  <input
                    type="text"
                    value={renameOldName}
                    onChange={(e) => setRenameOldName(e.target.value)}
                    list="conversation-variable-names"
                    style={{ width: '100%' }}
                  />
                  <datalist id="conversation-variable-names">
                    {variableNames.map((v) => (
                      <option key={v} value={v} />
                    ))}
                  </datalist>
                </div>
                <div className="form-row col-thin">
                  <label style={{ marginBottom: '8px', fontWeight: '500' }}>{t('conversation.renameNewLabel')}</label>
                  <input
                    type="text"
                    value={renameNewName}
                    onChange={(e) => setRenameNewName(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <div className="form-row col-full row-actions">
                  <button type="button" className="button button-large" onClick={() => setRenameModalVisible(false)}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="button button-large button-blue" disabled={renaming}>
                    {renaming ? t('common.saving') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <GenerateLeadsModal
        visible={generateLeadsModalVisible}
        onClose={() => setGenerateLeadsModalVisible(false)}
        conversationIds={graphDropoffIds}
        chatbotId={chatbotId}
        sourceGraphVar={graphHighlightVar}
        t={t}
      />
      </div>
  );
};
